import React, { useEffect, useRef } from 'react'
import RestaurantCard from '../../../../components/restaurant/RestaurantCard'
import ImpressionTracker from '../../../../components/analytics/ImpressionTracker'
import RestaurantCardSkeleton from '../../../../components/skeleton/RestaurantCardSkeleton'
import { logEvent } from '../../../../analytics/analytics'
import { ROUTES } from '../../../../helpers/routes-helper'
import { setRecommendationAttribution } from '../../../../helpers/recommendationAttribution'
import { DEFAULT_CARD_IMAGE } from '../../../../helpers/static-constants'
import useAppUtilities from '../../../../hooks/useAppUtilities'
import { useAppSelector } from '../../../../store'
import { selectPageSize } from '../../../../store/landingSlice'
import {
  selectMessage,
  selectSeverity,
  selectShouldOpen,
  setOpen,
} from '../../../../store/restaurantSlice'
import { RestaurantAndPredictedRestaurant } from '../../../../types'
import { MuiAlert, MuiSnackbar } from '../../styled/global.styled'
import {
  RestaurantListInnerGrid,
  RestaurantListMainContainer,
  RestaurantListMainGrid,
  RestaurantListWrapper,
} from '../styled/RestaurantList.styled'

interface RestaurantListProps {
  restaurants: RestaurantAndPredictedRestaurant[]
  loading: boolean
  // Attribution context for this ranked reco surface ("View All"). Threaded from the carousel via
  // nav state so this paginated continuation is attributable to the same recommendation instead of
  // arriving with a blank recommendation_id/rank. Omitted for non-reco lists (e.g. food areas).
  recommendationId?: string
  listType?: string
  modelVersion?: string
  screenName?: string
}

const RestaurantList: React.FC<RestaurantListProps> = ({
  restaurants,
  loading = false,
  recommendationId,
  listType,
  modelVersion,
  screenName = 'view_all',
}) => {
  const { navigate, dispatch } = useAppUtilities()
  const openServer = useAppSelector(selectShouldOpen)
  const message = useAppSelector(selectMessage)
  const severity = useAppSelector(selectSeverity)
  const pageSize = useAppSelector(selectPageSize)
  const viewedRecoRef = useRef<string | null>(null)

  const setOpenServer = (): void => {
    dispatch(setOpen(false))
  }

  // Mirror the carousel: emit recommendation_view once with the ranked restaurant IDs actually
  // shown so this reco surface is no longer invisible to the rank funnel. Keyed by recommendation_id
  // so pagination growth doesn't re-fire it.
  useEffect(() => {
    if (!loading && recommendationId && restaurants?.length > 0) {
      if (viewedRecoRef.current !== recommendationId) {
        viewedRecoRef.current = recommendationId
        const items = restaurants
          .map((c) => c?.restaurantDTO?.restaurantId ?? '')
          .filter(Boolean)
        logEvent('recommendation_view', {
          recommendation_id: recommendationId,
          items,
          list_type: listType,
          result_count: restaurants.length,
          model_version: modelVersion,
          screen_name: screenName,
        })
      }
    }
  }, [
    loading,
    recommendationId,
    listType,
    modelVersion,
    screenName,
    restaurants,
  ])

  return (
    <RestaurantListMainContainer>
      <RestaurantListMainGrid>
        {restaurants?.map((card, index: number) => {
          const alias = card?.restaurantDTO?.alias
          const restaurantId = card?.restaurantDTO?.restaurantId ?? ''
          return (
            <RestaurantListInnerGrid key={index}>
              <RestaurantListWrapper
                onClick={() => {
                  logEvent('restaurant_click', {
                    restaurant_id: restaurantId,
                    rank_position: index,
                    recommendation_id: recommendationId,
                    list_type: listType,
                    screen_name: screenName,
                  })
                  if (recommendationId) {
                    logEvent('recommendation_accepted', {
                      recommendation_id: recommendationId,
                      restaurant_id: restaurantId,
                      rank_position: index,
                      action: 'click',
                      list_type: listType,
                      model_version: modelVersion,
                      screen_name: screenName,
                    })
                    // Durable attribution fallback keyed by alias (see recommendationAttribution).
                    setRecommendationAttribution(alias, {
                      recommendationId,
                      rankPosition: index,
                      listType,
                      modelVersion,
                    })
                  }
                  navigate(
                    `${ROUTES.RESTAURANT_DETAILS}/${alias}/details`,
                    recommendationId
                      ? {
                          state: {
                            recommendation_id: recommendationId,
                            rank_position: index,
                            list_type: listType,
                            model_version: modelVersion,
                            source: 'home',
                          },
                        }
                      : undefined,
                  )
                }}
              >
                <ImpressionTracker
                  onImpress={() => {
                    logEvent('restaurant_impression', {
                      restaurant_id: restaurantId,
                      rank_position: index,
                      recommendation_id: recommendationId,
                      list_type: listType,
                      screen_name: screenName,
                    })
                  }}
                >
                  <RestaurantCard
                    id={card?.restaurantDTO?.restaurantId}
                    alias={card?.restaurantDTO?.alias ?? ''}
                    imgList={[
                      card?.restaurantDTO?.imageUrl ??
                      card?.restaurantDTO?.imageUrl !== ''
                        ? card?.restaurantDTO?.imageUrl ?? DEFAULT_CARD_IMAGE
                        : DEFAULT_CARD_IMAGE,
                    ]}
                    title={card?.restaurantDTO?.name ?? ''}
                    isVerified={card?.restaurantDTO?.verified ?? false}
                    subTitle={[
                      card?.restaurantDTO?.address1,
                      card?.restaurantDTO?.city,
                      card?.restaurantDTO?.state,
                    ]
                      .filter(Boolean)
                      .join(', ')}
                    distance={`${card?.restaurantDTO?.distance ?? ''}`}
                    recommendationId={recommendationId}
                    modelVersion={modelVersion}
                    phone={card?.restaurantDTO?.phone}
                    latitude={card?.restaurantDTO?.lat}
                    longitude={card?.restaurantDTO?.lng}
                    placeId={card?.restaurantDTO?.placeId}
                    address={[
                      card?.restaurantDTO?.address1,
                      card?.restaurantDTO?.city,
                      card?.restaurantDTO?.state,
                    ]
                      .filter(Boolean)
                      .join(', ')}
                    reservationUrl={card?.restaurantDTO?.reservationUrl}
                    website={card?.restaurantDTO?.website}
                    cuisine={card?.restaurantDTO?.cuisine ?? ''}
                    city={card?.restaurantDTO?.city ?? ''}
                    rankPosition={index}
                    plannerSourceSurface={screenName}
                    tagChipData={[
                      {
                        percentage: `${card.value?.toLocaleString() ?? ''}`,
                        title: 'Match',
                      },
                    ]}
                  />
                </ImpressionTracker>
              </RestaurantListWrapper>
            </RestaurantListInnerGrid>
          )
        })}
        {loading &&
          Array.from({ length: pageSize }).map((_, index) => {
            return (
              <RestaurantListInnerGrid key={index}>
                <RestaurantCardSkeleton />
              </RestaurantListInnerGrid>
            )
          })}
      </RestaurantListMainGrid>

      <MuiSnackbar open={openServer} onClose={setOpenServer}>
        <MuiAlert onClose={setOpenServer} severity={severity ?? 'success'}>
          {message}
        </MuiAlert>
      </MuiSnackbar>
    </RestaurantListMainContainer>
  )
}
export default RestaurantList
